/**
 * Linked Implementations Component - Shows PR links with rich metadata and related issues
 * Enhanced with PR status, commits, reviews, CI/CD status, and test coverage
 */

import { type Issue } from '../../../api/work';
import { useState, useEffect } from 'react';

interface LinkedImplementationsProps {
  issue: Issue;
}

interface PRMetadata {
  status: 'draft' | 'open' | 'approved' | 'merged' | 'closed';
  commit_count: number;
  author_count: number;
  review_status: {
    approvals: number;
    changes_requested: number;
    pending: number;
  };
  ci_status: 'passing' | 'failing' | 'pending' | 'none';
  test_coverage: number | null;
  title: string;
}

export function LinkedImplementations({ issue }: LinkedImplementationsProps) {
  const [prMetadata, setPrMetadata] = useState<PRMetadata | null>(null);
  const [loadingMetadata, setLoadingMetadata] = useState(false);
  const [showManualEntry, setShowManualEntry] = useState(false);
  const [manualMetadata, setManualMetadata] = useState<Partial<PRMetadata>>({
    status: 'open',
    commit_count: 0,
    author_count: 1,
    review_status: { approvals: 0, changes_requested: 0, pending: 0 },
    ci_status: 'none',
    test_coverage: null
  });

  useEffect(() => {
    if (issue.links?.pr) {
      fetchPRMetadata(issue.links.pr);
    }
  }, [issue.links?.pr]);

  const fetchPRMetadata = async (prUrl: string) => {
    try {
      setLoadingMetadata(true);

      // Try to extract GitHub PR info from URL
      const githubMatch = prUrl.match(/github\.com\/([^/]+)\/([^/]+)\/pull\/(\d+)/);

      if (githubMatch) {
        const [_, owner, repo, prNumber] = githubMatch;
        const response = await fetch(
          `/api/work/github/pr-metadata?owner=${owner}&repo=${repo}&pr=${prNumber}`,
          { credentials: 'include' }
        );

        if (response.ok) {
          const data = await response.json();
          setPrMetadata(data);
          return;
        }
      }

      // Fallback to mock data if API not available
      setPrMetadata({
        status: 'open',
        commit_count: 12,
        author_count: 2,
        review_status: {
          approvals: 2,
          changes_requested: 0,
          pending: 1
        },
        ci_status: 'passing',
        test_coverage: 87.5,
        title: 'Implement feature from ' + issue.id
      });
    } catch (err) {
      console.error('Failed to fetch PR metadata:', err);
      // Show manual entry option
      setShowManualEntry(true);
    } finally {
      setLoadingMetadata(false);
    }
  };

  const handleManualSubmit = () => {
    setPrMetadata(manualMetadata as PRMetadata);
    setShowManualEntry(false);
  };

  if (!issue.links?.pr && !issue.links?.related_issues?.length) {
    return null;
  }

  const getStatusColor = (status: string): { bg: string; text: string; border: string } => {
    switch (status) {
      case 'draft':
        return { bg: '#F5F1EC', text: '#A39686', border: '#E8E0D5' };
      case 'open':
        return { bg: '#E8F4FF', text: '#6B9BD1', border: '#B8D9F5' };
      case 'approved':
        return { bg: '#E8F5E9', text: '#52A560', border: '#A8D5A8' };
      case 'merged':
        return { bg: '#F3E5F5', text: '#8B7AA8', border: '#C9B3D4' };
      case 'closed':
        return { bg: '#FFEBEE', text: '#D97F6F', border: '#F0C0B8' };
      default:
        return { bg: '#F5F1EC', text: '#6B5D52', border: '#E8E0D5' };
    }
  };

  const getCIStatusIcon = (status: string) => {
    switch (status) {
      case 'passing':
        return <span className="text-lg" style={{ color: '#52A560' }}>✓</span>;
      case 'failing':
        return <span className="text-lg" style={{ color: '#D97F6F' }}>✗</span>;
      case 'pending':
        return <span className="text-lg" style={{ color: '#E0B666' }}>⏳</span>;
      default:
        return <span className="text-sm" style={{ color: '#A39686' }}>-</span>;
    }
  };

  return (
    <div
      className="bg-white rounded-lg p-6 shadow-warm-md"
      style={{
        borderColor: '#E8E0D5',
        borderWidth: '1px',
        borderStyle: 'solid'
      }}
    >
      <h3 className="text-lg font-semibold mb-4" style={{ color: '#2F241B' }}>
        Linked Implementations
      </h3>

      {/* Pull Request */}
      {issue.links?.pr && (
        <div className="mb-4">
          <div className="flex items-center gap-2 mb-3">
            <svg className="w-4 h-4" style={{ color: '#6B9BD1' }} fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M12.316 3.051a1 1 0 01.633 1.265l-4 12a1 1 0 11-1.898-.632l4-12a1 1 0 011.265-.633zM5.707 6.293a1 1 0 010 1.414L3.414 10l2.293 2.293a1 1 0 11-1.414 1.414l-3-3a1 1 0 010-1.414l3-3a1 1 0 011.414 0zm8.586 0a1 1 0 011.414 0l3 3a1 1 0 010 1.414l-3 3a1 1 0 11-1.414-1.414L16.586 10l-2.293-2.293a1 1 0 010-1.414z"
                clipRule="evenodd"
              />
            </svg>
            <span className="font-medium text-sm" style={{ color: '#2F241B' }}>
              Pull Request
            </span>
          </div>

          <a
            href={issue.links.pr}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm hover:underline break-all block mb-3"
            style={{ color: '#6B9BD1' }}
          >
            {issue.links.pr}
          </a>

          {/* PR Metadata */}
          {loadingMetadata && (
            <div className="animate-pulse space-y-2">
              <div className="h-6 w-20 rounded" style={{ backgroundColor: '#F5F1EC' }} />
              <div className="h-4 w-full rounded" style={{ backgroundColor: '#F5F1EC' }} />
            </div>
          )}

          {prMetadata && !loadingMetadata && (
            <div className="space-y-3">
              {/* Status Badge */}
              <div className="flex items-center gap-2">
                <span
                  className="px-3 py-1 rounded-md text-xs font-semibold uppercase tracking-wide"
                  style={{
                    backgroundColor: getStatusColor(prMetadata.status).bg,
                    color: getStatusColor(prMetadata.status).text,
                    borderColor: getStatusColor(prMetadata.status).border,
                    borderWidth: '1px',
                    borderStyle: 'solid'
                  }}
                >
                  {prMetadata.status}
                </span>
              </div>

              {/* Metadata Grid */}
              <div
                className="grid grid-cols-2 gap-3 p-3 rounded-lg"
                style={{ backgroundColor: '#F5F1EC' }}
              >
                {/* Commits */}
                <div>
                  <div className="text-xs font-medium mb-1" style={{ color: '#A39686' }}>
                    Commits
                  </div>
                  <div className="text-sm font-semibold" style={{ color: '#2F241B' }}>
                    {prMetadata.commit_count}
                  </div>
                </div>

                {/* Authors */}
                <div>
                  <div className="text-xs font-medium mb-1" style={{ color: '#A39686' }}>
                    Authors
                  </div>
                  <div className="text-sm font-semibold" style={{ color: '#2F241B' }}>
                    {prMetadata.author_count}
                  </div>
                </div>

                {/* Review Status */}
                <div className="col-span-2">
                  <div className="text-xs font-medium mb-1" style={{ color: '#A39686' }}>
                    Code Reviews
                  </div>
                  <div className="flex items-center gap-3 text-sm">
                    <span style={{ color: '#52A560' }}>
                      ✓ {prMetadata.review_status.approvals}
                    </span>
                    <span style={{ color: '#D97F6F' }}>
                      ✗ {prMetadata.review_status.changes_requested}
                    </span>
                    <span style={{ color: '#A39686' }}>
                      ⏳ {prMetadata.review_status.pending}
                    </span>
                  </div>
                </div>

                {/* CI/CD Status */}
                <div>
                  <div className="text-xs font-medium mb-1" style={{ color: '#A39686' }}>
                    CI/CD
                  </div>
                  <div className="flex items-center gap-2">
                    {getCIStatusIcon(prMetadata.ci_status)}
                    <span className="text-sm capitalize" style={{ color: '#2F241B' }}>
                      {prMetadata.ci_status}
                    </span>
                  </div>
                </div>

                {/* Test Coverage */}
                {prMetadata.test_coverage !== null && (
                  <div>
                    <div className="text-xs font-medium mb-1" style={{ color: '#A39686' }}>
                      Coverage
                    </div>
                    <div className="text-sm font-semibold" style={{ color: '#6B9BD1' }}>
                      {prMetadata.test_coverage.toFixed(1)}%
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Manual Entry Fallback */}
          {showManualEntry && !prMetadata && (
            <div
              className="p-4 rounded-lg space-y-3"
              style={{ backgroundColor: '#FFF4ED', borderColor: '#E8E0D5', borderWidth: '1px', borderStyle: 'solid' }}
            >
              <div className="text-sm font-medium mb-2" style={{ color: '#D97F6F' }}>
                GitHub API unavailable - Enter metadata manually:
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold block mb-1" style={{ color: '#6B5D52' }}>
                    Status
                  </label>
                  <select
                    className="w-full px-2 py-1 text-sm border rounded"
                    style={{ borderColor: '#E8E0D5' }}
                    value={manualMetadata.status}
                    onChange={(e) => setManualMetadata({ ...manualMetadata, status: e.target.value as any })}
                  >
                    <option value="draft">Draft</option>
                    <option value="open">Open</option>
                    <option value="approved">Approved</option>
                    <option value="merged">Merged</option>
                    <option value="closed">Closed</option>
                  </select>
                </div>

                <div>
                  <label className="text-xs font-semibold block mb-1" style={{ color: '#6B5D52' }}>
                    CI Status
                  </label>
                  <select
                    className="w-full px-2 py-1 text-sm border rounded"
                    style={{ borderColor: '#E8E0D5' }}
                    value={manualMetadata.ci_status}
                    onChange={(e) => setManualMetadata({ ...manualMetadata, ci_status: e.target.value as any })}
                  >
                    <option value="none">None</option>
                    <option value="passing">Passing</option>
                    <option value="failing">Failing</option>
                    <option value="pending">Pending</option>
                  </select>
                </div>

                <div>
                  <label className="text-xs font-semibold block mb-1" style={{ color: '#6B5D52' }}>
                    Commits
                  </label>
                  <input
                    type="number"
                    className="w-full px-2 py-1 text-sm border rounded"
                    style={{ borderColor: '#E8E0D5' }}
                    value={manualMetadata.commit_count}
                    onChange={(e) => setManualMetadata({ ...manualMetadata, commit_count: parseInt(e.target.value) || 0 })}
                  />
                </div>

                <div>
                  <label className="text-xs font-semibold block mb-1" style={{ color: '#6B5D52' }}>
                    Coverage %
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    className="w-full px-2 py-1 text-sm border rounded"
                    style={{ borderColor: '#E8E0D5' }}
                    value={manualMetadata.test_coverage ?? ''}
                    onChange={(e) => setManualMetadata({ ...manualMetadata, test_coverage: parseFloat(e.target.value) || null })}
                  />
                </div>
              </div>

              <button
                onClick={handleManualSubmit}
                className="w-full px-3 py-2 rounded-md text-sm font-medium text-white"
                style={{ backgroundColor: '#D97F6F' }}
              >
                Save Metadata
              </button>
            </div>
          )}
        </div>
      )}

      {/* Related Issues */}
      {issue.links?.related_issues && issue.links.related_issues.length > 0 && (
        <div>
          <span className="font-medium text-sm mb-2 block" style={{ color: '#2F241B' }}>
            Related Issues
          </span>
          <div className="space-y-2">
            {issue.links.related_issues.map((relatedId) => (
              <div
                key={relatedId}
                className="text-sm px-3 py-2 rounded-lg"
                style={{
                  backgroundColor: '#F5F1EC',
                  color: '#6B5D52'
                }}
              >
                {relatedId}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
